/**
 * Rebuild the presentation snapshot for the latest completed v2 run.
 *
 * The response log is the durable source when a learner opens a career on a
 * different device (or after local state has been cleared). This performs no
 * scoring: it only replays raw answers and the slot snapshots already written
 * by the assessment screens.
 *
 * Every reader shares one cached replay per learner: career pages mount and
 * remount constantly, and the replay costs two round trips. The cache is keyed
 * by learner, deduplicates concurrent callers, and expires after a minute so a
 * fresh run is picked up without a reload.
 */
import { supabase } from "@/integrations/supabase/client";
import { parseBand, type Band } from "./band";
import type { AssessmentV2State, Part1Answers } from "./types";

type JsonRecord = Record<string, unknown>;

export interface PersistedRun {
  /** Replayed state, shaped exactly like this device's local state. */
  state: AssessmentV2State;
  sessionId: string;
  completedAt: string | null;
}

const CACHE_TTL_MS = 60_000;

interface CacheEntry {
  learnerId: string;
  fetchedAt: number;
  run: PersistedRun | null;
}

let cache: CacheEntry | null = null;
let inflight: Promise<PersistedRun | null> | null = null;

/** Drop the cached replay — call after sealing a new run. */
export function clearPersistedRunCache(): void {
  cache = null;
  inflight = null;
}

function record(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as JsonRecord
    : {};
}

function answerValue(value: unknown): unknown {
  const wrapped = record(value);
  return Object.prototype.hasOwnProperty.call(wrapped, "value") ? wrapped.value : value;
}

/**
 * Latest completed backend run, with the metadata callers need to decide
 * whether the device's own state is out of date.
 */
export async function loadLatestCompletedRun(
  options: { force?: boolean; learnerId?: string } = {},
): Promise<PersistedRun | null> {
  const { data: auth } = await supabase.auth.getUser();
  const selfId = auth.user?.id;
  if (!selfId) return null;
  // Admin report viewer: another learner's run, never cached as our own.
  if (options.learnerId && options.learnerId !== selfId) {
    return fetchLatestCompletedRun(options.learnerId);
  }
  const learnerId = selfId;

  if (options.force) clearPersistedRunCache();

  if (
    cache &&
    cache.learnerId === learnerId &&
    Date.now() - cache.fetchedAt < CACHE_TTL_MS
  ) {
    return cache.run;
  }

  if (inflight) return inflight;

  inflight = fetchLatestCompletedRun(learnerId)
    .then((run) => {
      cache = { learnerId, fetchedAt: Date.now(), run };
      return run;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/** Backwards-compatible shape: just the replayed state. */
export async function loadLatestCompletedSnapshot(
  options: { force?: boolean } = {},
): Promise<AssessmentV2State | null> {
  const run = await loadLatestCompletedRun(options);
  return run?.state ?? null;
}

interface ReplayedRun extends PersistedRun {
  /** How much of the walk this session actually carries. */
  weight: number;
}

async function fetchLatestCompletedRun(learnerId: string): Promise<PersistedRun | null> {
  const { data: sessions, error: sessionError } = await supabase
    .from("runtime_sessions")
    .select("id, started_at, completed_at, metadata")
    .eq("learner_id", learnerId)
    .eq("status", "completed")
    .order("completed_at", { ascending: false })
    .limit(25);
  if (sessionError) throw sessionError;

  const candidates = (sessions ?? []).filter(
    (row) => record(row.metadata).source === "assessment-v2",
  );
  // Synthetic runs written by automated checks are never a learner's answers.
  const real = candidates.filter((row) => !record(row.metadata).seeded_by);
  const pool = (real.length > 0 ? real : candidates).slice(0, 10);
  if (pool.length === 0) return null;

  const { data: responses, error: responseError } = await supabase
    .from("assessment_v2_responses")
    .select("session_id, part, screen_id, raw_value, slot_writes, sequence, observed_at")
    .in("session_id", pool.map((row) => row.id))
    .order("sequence", { ascending: true })
    .order("observed_at", { ascending: true });
  if (responseError) throw responseError;
  if (!responses?.length) return null;

  const bySession = new Map<string, typeof responses>();
  for (const response of responses) {
    const bucket = bySession.get(response.session_id) ?? [];
    bucket.push(response);
    bySession.set(response.session_id, bucket);
  }

  // The newest completed session is not always the richest one: a run can be
  // sealed with a partial log. Replay each candidate, newest first, and keep
  // the first one that carries a usable walk — falling back to the fullest.
  let best: ReplayedRun | null = null;
  for (const session of pool) {
    const rows = bySession.get(session.id);
    if (!rows?.length) continue;
    const run = replaySession(session, rows);
    if (!best || run.weight > best.weight) best = run;
    if (run.weight >= USABLE_WEIGHT) break;
  }
  if (!best) return null;
  const { weight: _weight, ...run } = best;
  return run;
}

/** A walk that reached scoring writes both slot state and a real answer set. */
const USABLE_WEIGHT = 12;

function replaySession(
  session: { id: string; started_at: string | null; completed_at: string | null; metadata: unknown },
  responses: Array<{
    part: string | null;
    screen_id: string;
    raw_value: unknown;
    slot_writes: unknown;
  }>,
): ReplayedRun {
  const part1: Part1Answers = {};
  const part2: JsonRecord = {};
  let latestSlots: JsonRecord = {};
  let latestTally: JsonRecord = {};

  for (const response of responses) {
    const value = answerValue(response.raw_value);
    if (response.part === "part1") {
      Object.assign(part1, record(value));
    } else {
      part2[response.screen_id] = value;
    }

    // The answer row carries the two aggregates the walk had built by then:
    // the slot state and the archetype tally. The last row that carries each
    // one wins — they are cumulative, never partial.
    const { archetype_tally: tally, ...slots } = record(response.slot_writes);
    if (Object.keys(slots).length > 0) latestSlots = slots;
    if (Object.keys(record(tally)).length > 0) latestTally = record(tally);
  }

  const answerCount = Object.keys(part1).length + Object.keys(part2).length;
  if (Object.keys(latestSlots).length > 0) part2.slots = latestSlots;
  if (Object.keys(latestTally).length > 0) part2.archetype_tally = latestTally;

  const metadata = record(session.metadata);
  const startedAt = session.started_at ?? session.completed_at ?? new Date().toISOString();
  return {
    weight: answerCount + Object.keys(latestSlots).length,
    sessionId: session.id,
    completedAt: session.completed_at ?? null,
    state: {
      band: parseBand(typeof metadata.band === "string" ? metadata.band : null) as Band,
      currentScreen: "__end_part1__",
      part1,
      part2,
      startedAt,
      updatedAt: session.completed_at ?? startedAt,
    },
  };
}
